/**
 * Estado de consentimiento de cookies y tecnologías de medición.
 *
 * Ley 81/2019 (Panamá): el tratamiento de datos personales requiere
 * consentimiento previo, informado y revocable. El pixel de Meta, Google
 * Analytics y Clarity no se cargan hasta que la persona acepta su categoría.
 */

export type ConsentCategory = "analytics" | "marketing";

export type ConsentState = {
  /** Medición: Google Analytics 4, Google Tag Manager y Microsoft Clarity. */
  analytics: boolean;
  /** Publicidad: Meta Pixel. */
  marketing: boolean;
};

export type StoredConsent = ConsentState & {
  /** ISO timestamp de la decisión. */
  ts: string;
  version: number;
};

const STORAGE_KEY = "vivi-consent-v1";
const EVENT = "vivi:consent-change";

export const CONSENT_VERSION = 1;

const canUseStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

export const readConsent = (): StoredConsent | null => {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (
      typeof parsed.analytics !== "boolean" ||
      typeof parsed.marketing !== "boolean"
    ) {
      return null;
    }
    return {
      analytics: parsed.analytics,
      marketing: parsed.marketing,
      ts: typeof parsed.ts === "string" ? parsed.ts : "",
      version: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
};

export const saveConsent = (state: ConsentState): StoredConsent => {
  const stored: StoredConsent = {
    analytics: state.analytics,
    marketing: state.marketing,
    ts: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  if (canUseStorage()) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // Sin almacenamiento la preferencia vale solo para esta página.
    }
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: stored }));
  }

  return stored;
};

export const clearConsent = (): void => {
  if (canUseStorage()) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nada que limpiar.
    }
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: null }));
  }
};

export const onConsentChange = (
  listener: (state: StoredConsent | null) => void,
): (() => void) => {
  if (typeof window === "undefined") return () => {};

  const handler = (event: Event) => {
    const detail = (event as CustomEvent<StoredConsent | null>).detail;
    listener(detail === undefined ? readConsent() : detail);
  };

  const storageHandler = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) listener(readConsent());
  };

  window.addEventListener(EVENT, handler);
  window.addEventListener("storage", storageHandler);

  return () => {
    window.removeEventListener(EVENT, handler);
    window.removeEventListener("storage", storageHandler);
  };
};
